import { isSameDay } from 'date-fns';
import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { DayPartSections } from 'shared/types/page-sections';
import { SortingMode } from 'shared/types/sorting-mode';
import { TaskCardTask } from 'shared/types/task-card-task';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { isNumber } from 'shared/utils/is-number';
import { mapTaskToTaskCard } from 'shared/utils/mappers/map-task-to-task-card';

import { useDueStatusGroupedTasks } from './use-due-status-grouped-tasks';
import { useTodayTasks } from './use-today-tasks';

export const useDayPartTaskSections = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const tasks = useTodayTasks();
  const weekStartsOn = getWeekStartsOn(user);
  const timeFormat = getTimeFormat(user);

  const goals = useGoals();

  const { todayTasks, overdueTasks } = useDueStatusGroupedTasks(tasks);

  const sections = useMemo(
    () =>
      [
        {
          id: DayPartSections.Overdue,
          startTime: null,
          items: getSortedTasks(overdueTasks, {
            today,
            weekStartsOn,
            mode: [SortingMode.DueDate],
            invertedTimeslots: true,
            allDayFirst: true,
          }).map((task) =>
            mapTaskToTaskCard(task, {
              today,
              goals,
              t,
              showDue: true,
              hideTodayDue: true,
              weekStartsOn,
              timeFormat,
            }),
          ),
        },
        {
          id: DayPartSections.AllDay,
          startTime: null,
          items: getSortedTasks(
            todayTasks.filter((task) => {
              const schedule = getActiveSchedule(task.schedules);
              const dayPlannedOccurrence = schedule?.plannedOccurrences?.find(
                ({ date }) => isSameDay(date, today),
              );
              const timeslot = dayPlannedOccurrence
                ? dayPlannedOccurrence.timeslot
                : schedule
                  ? schedule.timeslot
                  : task.timeslot;
              return !isNumber(timeslot?.startTime);
            }),
            {
              today,
              weekStartsOn,
              mode: Array.from(
                new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
              ).filter(Boolean) as SortingMode[],
              order:
                user?.settings?.todayGroupSorting?.dayPart?.sorting?.[
                  DayPartSections.AllDay
                ] ?? [],
            },
          ).map((task) =>
            mapTaskToTaskCard(task, {
              today,
              goals,
              t,
              showDue: true,
              hideTodayDue: true,
              weekStartsOn,
              timeFormat,
            }),
          ),
        },
        {
          id: DayPartSections.Morning,
          startTime: 9 * 60 * 60 * 1000,
          items: getSortedTasks(
            todayTasks.filter((task) => {
              const schedule = getActiveSchedule(task.schedules);
              const dayPlannedOccurrence = schedule?.plannedOccurrences?.find(
                ({ date }) => isSameDay(date, today),
              );
              const timeslot = dayPlannedOccurrence
                ? dayPlannedOccurrence.timeslot
                : schedule
                  ? schedule.timeslot
                  : task.timeslot;

              if (!isNumber(timeslot?.startTime)) {
                return false;
              }

              const date = new Date();
              date.setHours(0, 0, 0, timeslot!.startTime!);

              return date.getHours() < 12;
            }),
            {
              today,
              weekStartsOn,
              mode: Array.from(
                new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
              ).filter(Boolean) as SortingMode[],
              order:
                user?.settings?.todayGroupSorting?.dayPart?.sorting?.[
                  DayPartSections.Morning
                ] ?? [],
            },
          ).map((task) =>
            mapTaskToTaskCard(task, {
              today,
              goals,
              t,
              showDue: true,
              hideTodayDue: true,
              weekStartsOn,
              timeFormat,
            }),
          ),
        },
        {
          id: DayPartSections.Afternoon,
          startTime: 12 * 60 * 60 * 1000,
          items: getSortedTasks(
            todayTasks.filter((task) => {
              const schedule = getActiveSchedule(task.schedules);
              const dayPlannedOccurrence = schedule?.plannedOccurrences?.find(
                ({ date }) => isSameDay(date, today),
              );
              const timeslot = dayPlannedOccurrence
                ? dayPlannedOccurrence.timeslot
                : schedule
                  ? schedule.timeslot
                  : task.timeslot;

              if (!isNumber(timeslot?.startTime)) {
                return false;
              }

              const date = new Date();
              date.setHours(0, 0, 0, timeslot!.startTime!);
              const startHour = date.getHours();

              return startHour >= 12 && startHour < 18;
            }),
            {
              today,
              weekStartsOn,
              mode: Array.from(
                new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
              ).filter(Boolean) as SortingMode[],
              order:
                user?.settings?.todayGroupSorting?.dayPart?.sorting?.[
                  DayPartSections.Afternoon
                ] ?? [],
            },
          ).map((task) =>
            mapTaskToTaskCard(task, {
              today,
              goals,
              t,
              showDue: true,
              hideTodayDue: true,
              weekStartsOn,
              timeFormat,
            }),
          ),
        },
        {
          id: DayPartSections.Evening,
          startTime: 18 * 60 * 60 * 1000,
          items: getSortedTasks(
            todayTasks.filter((task) => {
              const schedule = getActiveSchedule(task.schedules);
              const dayPlannedOccurrence = schedule?.plannedOccurrences?.find(
                ({ date }) => isSameDay(date, today),
              );
              const timeslot = dayPlannedOccurrence
                ? dayPlannedOccurrence.timeslot
                : schedule
                  ? schedule.timeslot
                  : task.timeslot;

              if (!isNumber(timeslot?.startTime)) {
                return false;
              }

              const date = new Date();
              date.setHours(0, 0, 0, timeslot!.startTime!);

              return date.getHours() >= 18;
            }),
            {
              today,
              weekStartsOn,
              mode: Array.from(
                new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
              ).filter(Boolean) as SortingMode[],
              order:
                user?.settings?.todayGroupSorting?.dayPart?.sorting?.[
                  DayPartSections.Evening
                ] ?? [],
            },
          ).map((task) =>
            mapTaskToTaskCard(task, {
              today,
              goals,
              t,
              showDue: true,
              hideTodayDue: true,
              weekStartsOn,
              timeFormat,
            }),
          ),
        },
      ].filter(
        ({ id, items }) => id !== DayPartSections.Overdue || items.length,
      ),
    [
      goals,
      overdueTasks,
      t,
      timeFormat,
      today,
      todayTasks,
      user?.settings?.todayGroupSorting?.dayPart?.sorting,
      user?.settings?.todoSortingMode,
      weekStartsOn,
    ],
  );

  const taskCardTasks = useMemo(
    () =>
      sections.reduce<TaskCardTask[]>((acc, { items }) => {
        acc.push(...items);
        return acc;
      }, []),
    [sections],
  );

  return {
    sections,
    taskCardTasks,
  };
};
